<template>
  <div class="flex items-center space-x-2 text-left">
    <div class="w-12 h-12 rounded-full flex justify-center items-center bg-vice-teal-600">
      <CloudGreenTick v-if="connectionStatus > 1" />
      <CloudRedWarning v-else />
    </div>

    <div>
      <div
        class="font-medium text-sm"
        :class="connectionStatus > 1 ? `text-white` : `text-red-500`"
      >
        {{ notificationTitle }}
      </div>
      <div class="text-gray-500 font-medium text-xxs">
        <span v-if="sessionStore.isClinician">{{ notification }}</span>
      </div>
    </div>
    <div>
      <RefreshIcon v-if="isRunning" class="ml-1 spin" />
    </div>
  </div>
</template>
<script>
import { onMounted, ref, watch } from 'vue';
import SpeedTest from '@cloudflare/speedtest';
import useSessionStore from '@/Stores/useSessionStore';
import RefreshIcon from '@/Components/Icons/RefreshIcon.vue';
import CloudGreenTick from '@/Components/Icons/CloudGreenTick.vue';
import CloudRedWarning from '@/Components/Icons/CloudRedWarning.vue';

export default {
  name: 'InternetSpeedBox',
  components: {
    CloudRedWarning,
    CloudGreenTick,
    RefreshIcon,
  },
  setup() {
    const sessionStore = useSessionStore();
    const isRunning = ref(null);
    const ableStreams = ref(0);
    const uploadSpeed = ref(0);
    const downloadSpeed = ref(0);
    const notification = ref('We are testing your connection...');
    const notificationTitle = ref('Estimation');
    const connectionStatus = ref(0);
    const connectionLabels = ['Insufficient', 'Limited', 'Normal', 'Good'];

    onMounted(() => {
      runChecks();
    });

    const runChecks = async () => {
      isRunning.value = true;
      const timeoutDuration = 35000;
      await Promise.all([withTimeout(checkInternetSpeed(), timeoutDuration)]);
      isRunning.value = false;
    };

    function withTimeout(promise, timeout) {
      return new Promise((resolve, reject) => {
        const timer = setTimeout(() => {
          console.error('Connection test timed out');
          resolve();
        }, timeout);

        promise
          .then((result) => {
            clearTimeout(timer);
            resolve(result);
          })
          .catch((error) => {
            clearTimeout(timer);
            reject(error);
          });
      });
    }

    const checkInternetSpeed = () =>
      new Promise((resolve) => {
        const speedTest = new SpeedTest();

        speedTest.onResultsChange = (result) => {
          if (result.type === 'download') {
            downloadSpeed.value = (speedTest.results.getSummary()[result.type] / 1000000).toFixed(
              2,
            );
          }

          if (result.type === 'upload') {
            uploadSpeed.value = (speedTest.results.getSummary()[result.type] / 1000000).toFixed(2);
          }
          setNotification();
        };

        speedTest.onFinish = () => {
          setNotification();
          resolve();
        };
      });

    function setNotification() {
      const maxDevices = 5;
      connectionStatus.value = getInetSpeedLabel();
      ableStreams.value = getBandwidthAbility(uploadSpeed.value, 3, maxDevices);
      notification.value = `enough for ${ableStreams.value} outgoing streams`;
      notificationTitle.value = `${connectionLabels[connectionStatus.value]} connection`;
    }

    function getBandwidthAbility(speed, costOne, max) {
      const number = speed / costOne;
      if (number >= max) return max;

      return Math.floor(number);
    }

    function getInetSpeedLabel() {
      if (downloadSpeed.value < 10 || uploadSpeed.value < 5) return 0;
      if (downloadSpeed.value < 20 || uploadSpeed.value < 10) return 1;
      if (downloadSpeed.value > 40 && uploadSpeed.value > 20) return 3;
      if (downloadSpeed.value > 20 && uploadSpeed.value > 10) return 2;

      return 4;
    }

    watch(isRunning, (newValue) => {
      if (newValue === false) setNotification();
    });

    return {
      isRunning,
      uploadSpeed,
      sessionStore,
      notification,
      downloadSpeed,
      connectionStatus,
      notificationTitle,
    };
  },
};
</script>
<style scoped>
.spin {
  animation: spin-animation 0.6s infinite reverse;
  display: inline-block;
}

@keyframes spin-animation {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
